// Einstellungen: Profil, Darstellung, Tagesbedarf, Mitteilungen, Geräte,
// Nutzerverwaltung (Admin) und der Zugang zur Statistik.
import type { AdaptiveTdee, EnergyProfile, UserView } from '@speis/shared';
import { api, logout, logoutEverywhere } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import * as push from '../core/push.js';
import { setState, state } from '../core/store.js';
import { THEME_LABEL, applyTheme, currentTheme } from '../core/theme.js';
import type { Theme } from '../core/theme.js';
import { append, clear, el, replace } from '../ui/dom.js';
import { alertDialog, confirmDialog } from '../ui/dialog.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import type { SheetHandle } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';
import { openStats } from './stats.js';

export async function openSettings(onSaved?: () => void): Promise<void> {
  const me = state.me;
  if (!me) {
    toast('Profil noch nicht geladen');
    return;
  }

  const body = el('div', {});
  const sheet = openSheet(body, { title: `Einstellungen · ${me.name}` });

  body.append(
    profileSection(me, sheet, onSaved),
    appearanceSection(),
    energySection(me),
    pushSection(),
    statsSection(sheet),
    devicesSection(),
  );

  if (me.role === 'admin') body.append(await usersSection());
  body.append(aboutSection());
}

function heading(title: string): HTMLElement {
  return el('h2', { class: 'settings-head' }, title);
}

// ── Profil ────────────────────────────────────────────────────────────

function profileSection(me: UserView, sheet: SheetHandle, onSaved?: () => void): HTMLElement {
  const name = el('input', { class: 'input', type: 'text', value: me.name });

  const mode = el('select', { class: 'select' });
  for (const [value, label] of [
    ['count', 'Kalorien zählen'],
    ['gentle', 'Sanft – keine Zahlen, abends ein Hinweis'],
  ] as const) {
    const option = el('option', { value }, label);
    if (me.calorieMode === value) option.selected = true;
    mode.append(option);
  }

  const goal = el('input', {
    class: 'input',
    type: 'number',
    inputmode: 'numeric',
    min: 500,
    max: 8000,
    value: me.dailyKcal ?? '',
    placeholder: 'z.B. 2200 (optional)',
  });

  return el('section', {}, [
    heading('Profil'),
    el('div', { class: 'field' }, [el('label', {}, 'Name'), name]),
    el('div', { class: 'field' }, [el('label', {}, 'Essen-Modus'), mode]),
    el('div', { class: 'field' }, [el('label', {}, 'Tagesziel kcal'), goal]),
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const { me: updated } = await api<{ me: UserView }>('/api/me', {
              method: 'PATCH',
              body: {
                name: name.value.trim() || me.name,
                calorieMode: mode.value,
                dailyKcal: goal.value === '' ? null : Number(goal.value),
              },
            });
            setState({ me: updated });
            sheet.setTitle(`Einstellungen · ${updated.name}`);
            onSaved?.();
          },
          { success: 'Gespeichert', failure: 'Speichern fehlgeschlagen' },
        ),
      },
      'Profil speichern',
    ),
  ]);
}

// ── Darstellung ───────────────────────────────────────────────────────

function appearanceSection(): HTMLElement {
  const row = el('div', { class: 'chip-row' });
  const themes: Theme[] = ['auto', 'light', 'dark'];

  for (const theme of themes) {
    row.append(
      el(
        'button',
        {
          class: 'chip',
          type: 'button',
          'aria-pressed': String(currentTheme() === theme),
          onclick: guarded(async () => {
            applyTheme(theme);
            for (const chip of row.children) chip.setAttribute('aria-pressed', 'false');
            row.children[themes.indexOf(theme)]?.setAttribute('aria-pressed', 'true');
            // Damit ein zweites Gerät gleich aussieht.
            await api('/api/me', { method: 'PATCH', body: { theme } });
          }),
        },
        [
          icon(theme === 'dark' ? 'moon' : theme === 'light' ? 'sun' : 'settings', { size: 15 }),
          THEME_LABEL[theme],
        ],
      ),
    );
  }

  return el('section', {}, [
    heading('Darstellung'),
    row,
    el('p', { class: 'field-hint' }, '„Automatisch" folgt der Einstellung des Geräts.'),
  ]);
}

// ── Tagesbedarf ───────────────────────────────────────────────────────

function energySection(me: UserView): HTMLElement {
  const weight = el('input', {
    class: 'input',
    type: 'number',
    inputmode: 'decimal',
    value: me.weightKg ?? '',
    placeholder: 'Gewicht kg',
  });
  const height = el('input', {
    class: 'input',
    type: 'number',
    inputmode: 'numeric',
    value: me.heightCm ?? '',
    placeholder: 'Größe cm',
  });
  const birth = el('input', {
    class: 'input',
    type: 'number',
    inputmode: 'numeric',
    value: me.birthYear ?? '',
    placeholder: 'Geburtsjahr',
  });

  const sex = el('select', { class: 'select' });
  for (const [value, label] of [
    ['', '– Geschlecht –'],
    ['m', 'männlich'],
    ['f', 'weiblich'],
  ] as const) {
    const option = el('option', { value }, label);
    if ((me.sex ?? '') === value) option.selected = true;
    sex.append(option);
  }

  const activity = el('select', { class: 'select' });
  const goalSelect = el('select', { class: 'select' });
  const output = el('div', { class: 'energy-out' });
  const adaptiveOut = el('div', { class: 'energy-out' });

  function fill(
    select: HTMLSelectElement,
    options: { value: string; label: string }[],
    current: string | null,
    placeholder: string,
  ): void {
    clear(select);
    select.append(el('option', { value: '' }, placeholder));
    for (const o of options) {
      const option = el('option', { value: o.value }, o.label);
      if (current === o.value) option.selected = true;
      select.append(option);
    }
  }

  function showEnergy(energy: EnergyProfile): void {
    clear(output);
    if (!energy.complete) {
      for (const warning of energy.warnings) {
        output.append(el('p', { class: 'notice notice-warn' }, `⚠️ ${warning}`));
      }
      if (!energy.warnings.length) {
        output.append(el('p', { class: 'field-hint' }, 'Bitte alle Felder ausfüllen.'));
      }
      return;
    }

    append(output, [
      el(
        'p',
        { class: 'tabular' },
        `Grundumsatz ${energy.bmr} kcal · Gesamtumsatz ${energy.tdee} kcal`,
      ),
      el('p', { class: 'energy-target tabular' }, `Empfohlenes Tagesziel: ${energy.target} kcal`),
      energy.protein
        ? el(
            'p',
            { class: 'tiny' },
            `Protein-Richtwert (${energy.protein.why}): ${energy.protein.minG}–${energy.protein.maxG} g/Tag`,
          )
        : null,
      ...energy.warnings.map((w) => el('p', { class: 'notice notice-warn' }, `⚠️ ${w}`)),
      el(
        'p',
        { class: 'tiny' },
        `Basis: ${energy.basis} – Schätzformel mit ±10 % Streuung, kein medizinischer Rat. Bei Erkrankungen oder Essstörungen bitte ärztlich begleiten lassen.`,
      ),
      el(
        'button',
        {
          class: 'btn btn-secondary btn-block',
          type: 'button',
          onclick: guarded(
            async () => {
              const { me: updated } = await api<{ me: UserView }>('/api/me', {
                method: 'PATCH',
                body: { dailyKcal: energy.target },
              });
              setState({ me: updated });
            },
            { success: `Tagesziel auf ${energy.target} kcal gesetzt` },
          ),
        },
        `${energy.target} kcal als Tagesziel übernehmen`,
      ),
    ]);
  }

  function showAdaptive(adaptive: AdaptiveTdee): void {
    clear(adaptiveOut);
    if (!adaptive.reliable) {
      append(adaptiveOut, [
        el(
          'p',
          { class: 'tiny' },
          `Kalibrierung aus deinen Daten: noch nicht möglich (${adaptive.reason}). Regelmäßig wiegen und Essen loggen – nach zwei bis vier Wochen wird das Ziel präziser als jede Formel.`,
        ),
      ]);
      return;
    }
    const trend =
      (adaptive.trendKgPerWeek ?? 0) > 0
        ? `+${adaptive.trendKgPerWeek}`
        : `${adaptive.trendKgPerWeek}`;
    append(adaptiveOut, [
      el(
        'p',
        { class: 'energy-target tabular' },
        `Dein echter Verbrauch: ~${adaptive.tdee} kcal/Tag`,
      ),
      el(
        'p',
        { class: 'tiny' },
        `Aus ${adaptive.days} Tagen (${adaptive.daysLogged} geloggt), Gewichtstrend ${trend} kg/Woche. Energiebilanz-Methode – genauer als die Formel, weil aus deinen Daten.`,
      ),
      adaptive.target
        ? el(
            'button',
            {
              class: 'btn btn-secondary btn-block',
              type: 'button',
              onclick: guarded(
                async () => {
                  const { me: updated } = await api<{ me: UserView }>('/api/me', {
                    method: 'PATCH',
                    body: { dailyKcal: adaptive.target },
                  });
                  setState({ me: updated });
                },
                { success: `Tagesziel auf ${adaptive.target} kcal kalibriert` },
              ),
            },
            `Kalibriertes Ziel übernehmen: ${adaptive.target} kcal`,
          )
        : null,
    ]);
  }

  async function loadEnergy(): Promise<void> {
    const data = await guard(
      () =>
        api<{
          energy: EnergyProfile;
          adaptive: AdaptiveTdee;
          options: {
            activity: { value: string; label: string }[];
            goal: { value: string; label: string }[];
          };
        }>('/api/me/energy'),
      { failure: 'Tagesbedarf' },
    );
    if (!data) return;
    fill(activity, data.options.activity, me.activity, '– Aktivität –');
    fill(goalSelect, data.options.goal, me.goal, '– Ziel –');
    showEnergy(data.energy);
    showAdaptive(data.adaptive);
  }

  void loadEnergy();

  return el('section', {}, [
    heading('Tagesbedarf berechnen'),
    el('div', { class: 'grid-2' }, [weight, height, birth, sex, activity, goalSelect]),
    el(
      'button',
      {
        class: 'btn btn-secondary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const { me: updated } = await api<{ me: UserView }>('/api/me', {
              method: 'PATCH',
              body: {
                weightKg: weight.value ? Number(weight.value) : null,
                heightCm: height.value ? Number(height.value) : null,
                birthYear: birth.value ? Number(birth.value) : null,
                sex: sex.value || null,
                activity: activity.value || null,
                goal: goalSelect.value || null,
              },
            });
            setState({ me: updated });
            await loadEnergy();
          },
          { failure: 'Berechnen fehlgeschlagen' },
        ),
      },
      [icon('chart', { size: 18 }), 'Berechnen'],
    ),
    output,
    adaptiveOut,
  ]);
}

// ── Mitteilungen ──────────────────────────────────────────────────────

function pushSection(): HTMLElement {
  const section = el('section', {}, [heading('Mitteilungen')]);
  const content = el('div', {});
  section.append(content);

  void (async () => {
    const config = await guard(() => push.getConfig(), { failure: 'Mitteilungen' });
    if (!config) return;

    const blocked = push.pushBlockedReason(config);
    if (blocked) {
      replace(content, el('p', { class: 'notice notice-warn' }, blocked));
      return;
    }

    const subscribed = await push.isSubscribed();

    const toggle = el('button', {
      class: 'switch',
      type: 'button',
      role: 'switch',
      'aria-checked': String(subscribed),
      'aria-label': 'Mitteilungen auf diesem Gerät',
    });
    toggle.addEventListener(
      'click',
      guarded(async () => {
        const on = toggle.getAttribute('aria-checked') === 'true';
        if (on) {
          await push.unsubscribe();
          toggle.setAttribute('aria-checked', 'false');
          toast('Mitteilungen aus');
        } else {
          const ok = await push.subscribe(config.publicKey);
          toggle.setAttribute('aria-checked', String(ok));
          toast(ok ? 'Mitteilungen an' : 'Berechtigung nicht erteilt');
        }
      }),
    );

    const kinds = el('div', {});
    for (const kind of config.kinds) {
      const kindToggle = el('button', {
        class: 'switch',
        type: 'button',
        role: 'switch',
        'aria-checked': String(config.prefs[kind.key] ?? false),
        'aria-label': kind.label,
      });
      kindToggle.addEventListener(
        'click',
        guarded(async () => {
          const next = kindToggle.getAttribute('aria-checked') !== 'true';
          kindToggle.setAttribute('aria-checked', String(next));
          await push.savePrefs({ prefs: { [kind.key]: next } });
        }),
      );
      kinds.append(
        el('div', { class: 'switch-row' }, [
          el('span', { class: 'switch-label' }, kind.label),
          kindToggle,
        ]),
      );
    }

    const quiet = el('input', {
      class: 'input',
      type: 'text',
      value: config.quietHours ?? '',
      placeholder: 'z.B. 22:00-07:00',
      'aria-label': 'Ruhezeiten',
    });
    quiet.addEventListener(
      'change',
      guarded(
        async () => {
          await push.savePrefs({ quietHours: quiet.value.trim() || null });
        },
        { success: 'Ruhezeiten gespeichert' },
      ),
    );

    replace(content, [
      el('div', { class: 'switch-row' }, [
        el('span', { class: 'switch-label' }, 'Auf diesem Gerät'),
        toggle,
      ]),
      kinds,
      el('div', { class: 'field' }, [el('label', {}, 'Ruhezeiten'), quiet]),
      el(
        'button',
        {
          class: 'btn btn-secondary btn-block',
          type: 'button',
          onclick: guarded(
            async () => {
              const { sent } = await push.sendTest();
              toast(sent ? 'Testnachricht unterwegs' : 'Es war kein Gerät angemeldet');
            },
            { failure: 'Test fehlgeschlagen' },
          ),
        },
        [icon('bell', { size: 18 }), 'Testnachricht senden'],
      ),
    ]);
  })();

  return section;
}

// ── Statistik ─────────────────────────────────────────────────────────

function statsSection(sheet: SheetHandle): HTMLElement {
  return el('section', {}, [
    heading('Auswertungen'),
    el(
      'button',
      {
        class: 'btn btn-secondary btn-block',
        type: 'button',
        onclick: () => {
          sheet.close();
          void openStats();
        },
      },
      [icon('chart', { size: 18 }), 'Ausgaben, Vorrat & Verschwendung'],
    ),
  ]);
}

// ── Geräte ────────────────────────────────────────────────────────────

function devicesSection(): HTMLElement {
  const list = el('div', {});
  const section = el('section', {}, [heading('Angemeldete Geräte'), list]);

  void (async () => {
    const data = await guard(
      () =>
        api<{
          sessions: { id: string; userAgent: string | null; lastSeen: string; current: boolean }[];
        }>('/api/me'),
      { failure: 'Geräte' },
    );
    if (!data) return;

    replace(
      list,
      data.sessions.map((s) =>
        el('div', { class: 'device-row' }, [
          el('div', { class: 'item-body' }, [
            el('div', { class: 'item-name' }, describeDevice(s.userAgent)),
            el('div', { class: 'item-meta' }, [
              s.current ? el('span', { class: 'badge badge-brand' }, 'dieses Gerät') : null,
              el('span', { class: 'tiny' }, `zuletzt ${s.lastSeen.slice(0, 16).replace('T', ' ')}`),
            ]),
          ]),
          s.current
            ? null
            : el(
                'button',
                {
                  class: 'btn btn-sm btn-danger',
                  type: 'button',
                  onclick: guarded(
                    async (e) => {
                      await api(`/api/me/sessions/${s.id}`, { method: 'DELETE' });
                      (e.currentTarget as HTMLElement).closest('.device-row')?.remove();
                    },
                    { success: 'Gerät abgemeldet' },
                  ),
                },
                'Abmelden',
              ),
        ]),
      ),
    );
  })();

  section.append(
    el(
      'button',
      {
        class: 'btn btn-secondary btn-block',
        type: 'button',
        onclick: guarded(async () => {
          await logout();
          location.reload();
        }),
      },
      [icon('logout', { size: 18 }), 'Auf diesem Gerät abmelden'],
    ),
    el(
      'button',
      {
        class: 'btn btn-danger btn-block',
        type: 'button',
        onclick: async () => {
          const ok = await confirmDialog({
            title: 'Überall abmelden?',
            message:
              'Alle Geräte müssen sich danach neu anmelden – sinnvoll, wenn ein Telefon abhandengekommen ist.',
            confirmLabel: 'Überall abmelden',
            danger: true,
          });
          if (!ok) return;
          await guard(async () => {
            await logoutEverywhere();
            location.reload();
          });
        },
      },
      'Überall abmelden',
    ),
  );

  return section;
}

function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unbekanntes Gerät';
  if (/iPhone/.test(userAgent)) return 'iPhone';
  if (/iPad/.test(userAgent)) return 'iPad';
  if (/Android/.test(userAgent)) return 'Android-Gerät';
  if (/Macintosh/.test(userAgent)) return 'Mac';
  if (/Windows/.test(userAgent)) return 'Windows-PC';
  return userAgent.slice(0, 40);
}

// ── Nutzerverwaltung ──────────────────────────────────────────────────

async function usersSection(): Promise<HTMLElement> {
  const list = el('div', {});
  const section = el('section', {}, [heading('Nutzerverwaltung'), list]);

  async function draw(): Promise<void> {
    const data = await guard(() => api<{ users: UserView[] }>('/api/users'), { failure: 'Nutzer' });
    if (!data) return;

    replace(
      list,
      data.users.map((u) =>
        el('div', { class: 'device-row' }, [
          el('div', { class: 'item-body' }, [
            el('div', { class: 'item-name' }, u.name),
            u.role === 'admin' ? el('span', { class: 'badge badge-brand' }, 'Admin') : null,
          ]),
          u.role === 'admin'
            ? null
            : el('div', { class: 'row-actions' }, [
                el(
                  'button',
                  {
                    class: 'btn btn-sm btn-secondary',
                    type: 'button',
                    onclick: guarded(async () => {
                      const { user } = await api<{ user: UserView }>(
                        `/api/users/${u.id}/rotate-token`,
                        { method: 'POST' },
                      );
                      await showLoginLink(user);
                    }),
                  },
                  'Neuer Link',
                ),
                el(
                  'button',
                  {
                    class: 'btn btn-sm btn-danger',
                    type: 'button',
                    onclick: async () => {
                      const ok = await confirmDialog({
                        title: `${u.name} löschen?`,
                        message: 'Kalorien-Tagebuch und Favoriten dieser Person verschwinden mit.',
                        confirmLabel: 'Löschen',
                        danger: true,
                      });
                      if (!ok) return;
                      await guard(
                        async () => {
                          await api(`/api/users/${u.id}`, { method: 'DELETE' });
                          await draw();
                        },
                        { success: 'Gelöscht' },
                      );
                    },
                  },
                  'Löschen',
                ),
              ]),
        ]),
      ),
    );
  }

  const newName = el('input', { class: 'input', type: 'text', placeholder: 'Name, z.B. Anna' });
  section.append(
    el('div', { class: 'field' }, [el('label', {}, 'Neue Person'), newName]),
    el(
      'button',
      {
        class: 'btn btn-secondary btn-block',
        type: 'button',
        onclick: guarded(async () => {
          if (!newName.value.trim()) return;
          const { user } = await api<{ user: UserView }>('/api/users', {
            method: 'POST',
            body: { name: newName.value.trim() },
          });
          newName.value = '';
          await draw();
          await showLoginLink(user);
        }),
      },
      [icon('plus', { size: 18 }), 'Person anlegen'],
    ),
    el(
      'p',
      { class: 'field-hint' },
      'Zugangstokens liegen nur als Hash in der Datenbank. Ein Link lässt sich deshalb neu erzeugen, aber nicht nachschlagen – auch nicht aus einer Sicherung.',
    ),
  );

  await draw();
  return section;
}

async function showLoginLink(user: UserView): Promise<void> {
  if (!user.token) return;
  const link = `${location.origin}/?token=${user.token}`;
  await navigator.clipboard?.writeText(link).catch(() => {});
  await alertDialog(
    `Login-Link für ${user.name}`,
    `${link}\n\n(kopiert) Dieser Link ist nur jetzt sichtbar. Später lässt sich nur ein neuer erzeugen.`,
  );
}

// ── Über ──────────────────────────────────────────────────────────────

function aboutSection(): HTMLElement {
  const info = el('p', { class: 'tiny' }, 'Version wird geladen …');
  void fetch('/health')
    .then((r) => r.json())
    .then((h: { version: string; schemaVersion: number }) => {
      info.textContent = `Speis ${h.version} · Datenbankschema v${h.schemaVersion}`;
    })
    .catch(() => {
      info.textContent = 'Version nicht abrufbar (offline).';
    });

  return el('section', {}, [heading('Über'), info]);
}
